// Layer: Component (UI) – chỉ render JSX, gọi hook domain từ features.
import React from 'react';
import { Tabs, Avatar, Button, Empty } from 'antd';
import { UserOutlined, FileTextOutlined, EyeOutlined, TeamOutlined } from '@ant-design/icons';
import { buildFullUrl } from '../../contexts/AvatarContext';
import { useHeaderSearch, type TimesheetResult, type DocumentResult } from '@/features/search/useHeaderSearch';
import styles from './SearchDropdown.module.css';

interface SearchDropdownProps {
  searchQuery: string;
  onSelect?: () => void;
}

// Component để hiển thị avatar với xác thực
interface PersonnelAvatarProps {
  avatarUrl: string | null | undefined;
  fetchAvatarAsDataUri: (url: string) => Promise<string | null>;
  cacheRef: React.MutableRefObject<Map<string, string>>;
}

const PersonnelAvatar: React.FC<PersonnelAvatarProps> = ({ avatarUrl, fetchAvatarAsDataUri, cacheRef }) => {
  const [avatarDataUri, setAvatarDataUri] = React.useState<string | null>(null);
  const [avatarError, setAvatarError] = React.useState(false);
  const [useDirectUrl, setUseDirectUrl] = React.useState(false);

  React.useEffect(() => {
    // Reset state khi avatarUrl thay đổi
    setAvatarDataUri(null);
    setAvatarError(false);
    setUseDirectUrl(false);

    if (!avatarUrl) {
      return;
    }

    const isValidUrl = avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://');
    if (!isValidUrl) {
      return;
    }

    // Kiểm tra cache trước
    if (cacheRef.current.has(avatarUrl)) {
      const cached = cacheRef.current.get(avatarUrl);
      if (cached) {
        setAvatarDataUri(cached);
        setAvatarError(false);
        return;
      }
    }

    fetchAvatarAsDataUri(avatarUrl)
      .then((dataUri) => {
        if (dataUri) {
          // Cache the result
          cacheRef.current.set(avatarUrl, dataUri);
          setAvatarDataUri(dataUri);
          setAvatarError(false);
          setUseDirectUrl(false);
        } else {
          // Fallback: dùng URL trực tiếp nếu fetch data URI thất bại
          setUseDirectUrl(true);
          setAvatarError(false);
        }
      })
      .catch((error) => {
        console.error('Error fetching avatar:', error);
        // Fallback: dùng URL trực tiếp
        setUseDirectUrl(true);
        setAvatarError(false);
      });
  }, [avatarUrl, fetchAvatarAsDataUri, cacheRef]);

  // Sử dụng data URI nếu có, nếu không thì dùng URL trực tiếp (fallback)
  const avatarSrc = avatarDataUri && !avatarError ? avatarDataUri : (useDirectUrl && avatarUrl ? avatarUrl : undefined);
  const showAvatar = !!avatarSrc;

  return (
    <Avatar 
      size={40} 
      src={showAvatar ? avatarSrc : undefined}
      icon={!showAvatar ? <UserOutlined /> : undefined}
      className={styles.avatar}
      onError={() => {
        // Nếu đang dùng data URI mà lỗi, thử fallback về URL trực tiếp
        if (avatarDataUri && !useDirectUrl) {
          setUseDirectUrl(true);
          setAvatarDataUri(null);
          setAvatarError(false);
        } else {
          setAvatarError(true);
        }
        return false;
      }}
    />
  );
};

export const SearchDropdown: React.FC<SearchDropdownProps> = ({ searchQuery, onSelect }) => {
  const {
    activeTab,
    setActiveTab,
    filteredResults,
    counts,
    loading,
    avatarCacheRef,
    fetchAvatarAsDataUri,
    handlePersonnelClick,
    handleDepartmentClick,
    handleDocumentClick,
  } = useHeaderSearch(searchQuery);

  // Sync searchQuery với hook khi prop thay đổi
  React.useEffect(() => {
    // Hook sẽ tự động sync searchQuery qua useEffect bên trong
  }, [searchQuery]);

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Tab items với counts
  const tabItems = [
    { key: 'all', label: 'Tất cả', count: counts.all },
    { key: 'timesheets', label: 'Bảng công', count: counts.timesheets },
    { key: 'documents', label: 'Đơn từ', count: counts.documents },
    { key: 'personnel', label: 'Nhân sự', count: counts.personnel },
    { key: 'departments', label: 'Phòng ban', count: counts.departments },
  ].map(({ key, label, count }) => ({
    key,
    label: (
      <span>
        {label}
        {count > 0 && <span className={styles.countBadge}>{count}</span>}
      </span>
    ),
  }));

  const hasResults = filteredResults.personnel.length > 0 || filteredResults.timesheets.length > 0 || 
                     filteredResults.documents.length > 0 || filteredResults.departments.length > 0;

  if (!searchQuery.trim() && counts.all === 0) {
    return null;
  }

  // Component hỗ trợ cho mục kết quả tài liệu/bảng chấm công
  const DocumentResultItem: React.FC<{
    item: TimesheetResult | DocumentResult;
    icon: React.ReactNode;
    onClick: () => void;
  }> = ({ item, icon, onClick }) => (
    <div key={item.id} className={styles.resultItem} onClick={onClick}>
      <div className={styles.documentIcon}>{icon}</div>
      <div className={styles.resultContent}>
        <div className={styles.resultTitle}>{item.title}</div>
        <div className={styles.resultDescription}>{item.description}</div>
      </div>
      <div className={styles.viewIcon}>
        <EyeOutlined />
      </div>
    </div>
  );

  // Wrapper handlers để gọi onSelect callback
  const handlePersonnelClickWithCallback = (personnel: any) => {
    handlePersonnelClick(personnel);
    onSelect?.();
  };

  const handleDepartmentClickWithCallback = () => {
    handleDepartmentClick();
    onSelect?.();
  };

  const handleDocumentClickWithCallback = (document: any) => {
    handleDocumentClick(document);
    onSelect?.();
  };

  return (
    <div className={styles.dropdown} onClick={stopPropagation} onMouseDown={stopPropagation}>
      <div className={styles.dropdownContent}>
        <div onClick={(e) => { e.stopPropagation(); e.preventDefault(); }} onMouseDown={stopPropagation}>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as any)}
            items={tabItems}
            className={styles.tabs}
          />
        </div>

        <div className={styles.resultsList}>
          {filteredResults.personnel.length > 0 && (
            <div className={styles.personnelSection}>
              {filteredResults.personnel.map((person) => (
                <div key={person.id} className={styles.resultItem}>
                  <PersonnelAvatar
                    avatarUrl={buildFullUrl(person.avatar)}
                    fetchAvatarAsDataUri={fetchAvatarAsDataUri}
                    cacheRef={avatarCacheRef}
                  />
                  <div className={styles.resultContent}>
                    <div className={styles.resultTitle}>{person.fullName}</div>
                    <div className={styles.resultDescription}>
                      {person.employeeCode ? `Mã: ${person.employeeCode} - ` : ''}
                      {person.jobTitle || person.position}
                    </div>
                  </div>
                  <Button
                    type="link"
                    className={styles.viewButton}
                    onClick={() => handlePersonnelClickWithCallback(person)}
                  >
                    Xem hồ sơ
                  </Button>
                </div>
              ))}
            </div>
          )}

          {filteredResults.timesheets.length > 0 && (
            <div className={styles.documentsSection}>
              {filteredResults.timesheets.map((timesheet) => (
                <DocumentResultItem
                  key={timesheet.id}
                  item={timesheet}
                  icon={<FileTextOutlined />}
                  onClick={() => handleDocumentClickWithCallback(timesheet)}
                />
              ))}
            </div>
          )}

          {filteredResults.documents.length > 0 && (
            <div className={styles.documentsSection}>
              {filteredResults.documents.map((doc) => (
                <DocumentResultItem
                  key={doc.id}
                  item={doc}
                  icon={<FileTextOutlined />}
                  onClick={() => handleDocumentClickWithCallback(doc)}
                />
              ))}
            </div>
          )}

          {filteredResults.departments.length > 0 && (
            <div className={styles.documentsSection}>
              {filteredResults.departments.map((dept) => (
                <div key={dept.id} className={styles.resultItem} onClick={handleDepartmentClickWithCallback}>
                  <div className={styles.documentIcon}>
                    <TeamOutlined />
                  </div>
                  <div className={styles.resultContent}>
                    <div className={styles.resultTitle}>{dept.name}</div>
                    <div className={styles.resultDescription}>
                      {dept.code ? `Mã: ${dept.code} - ${dept.description || `Phòng ban ${dept.name}`}` : dept.description || `Phòng ban ${dept.name}`}
                    </div>
                  </div>
                  <div className={styles.viewIcon}>
                    <EyeOutlined />
                  </div>
                </div>
              ))}
            </div>
          )}

          {searchQuery.trim() && !hasResults && !loading && (
            <div className={styles.emptyState}>
              <Empty
                description="Không tìm thấy kết quả"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
